/**
 * Upload Service
 * Dedicated to all media upload operations.
 * Kept separate for future compression, watermarking, and pre-processing pipelines.
 */
import axiosClient from "../api/axiosClient";
import { checkCompanion } from "./videoCompressionService";
import { resolveMetaDateTimes } from "../utils/mediaDateTime";
import { normalizeUploadFolderName } from "../utils/helpers";

const COMPANION_DIRECT = "http://localhost:7891";
const COMPANION_URL = process.env.NODE_ENV === "development" ? "/companion-api" : COMPANION_DIRECT;

const normalizeStudioToken = (token) => String(token || "")
  .trim()
  .replace(/^Bearer\s+/i, "");

const resolveStudioApiBaseUrl = (baseUrl) => {
  const raw = String(baseUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, "");
  try {
    return new URL(raw, window.location.origin).toString().replace(/\/+$/, "");
  } catch {
    return "";
  }
};

/**
 * Push PP auth context to Studio so Studio can fetch events/galleries/albums without manual token entry.
 */
export const syncCompanionSession = async () => {
  const authToken = normalizeStudioToken(localStorage.getItem("token") || "");
  const apiBaseUrl = resolveStudioApiBaseUrl(axiosClient.defaults.baseURL || "/api");
  if (!authToken || !apiBaseUrl) return false;

  const payload = JSON.stringify({ apiBaseUrl, authToken });
  const targets = [
    `${COMPANION_DIRECT}/studio/session`,
    `${COMPANION_URL}/studio/session`,
  ];

  for (const url of targets) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      if (res.ok) return true;
    } catch {
      // Try the next target.
    }
  }

  return false;
};

export const clearCompanionSession = async () => {
  const targets = [
    `${COMPANION_DIRECT}/studio/session`,
    `${COMPANION_URL}/studio/session`,
  ];

  for (const url of targets) {
    try {
      const res = await fetch(url, { method: "DELETE" });
      if (res.ok) return true;
    } catch {
      // Try the next target.
    }
  }

  return false;
};

const appendMetaDateTimes = (formData, metaDateTimes) => {
  metaDateTimes.forEach((dateTime, index) => {
    formData.append(`metaDateTimes[${index}]`, dateTime);
  });
};

const triggerBlobDownload = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName || "download.bin";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delay revocation so slower browsers can complete the save handshake.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const readResponseAsBlob = async (res) => {
  if (res.body && res.body.getReader) {
    const reader = res.body.getReader();
    const chunks = [];
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    return new Blob(chunks, {
      type: res.headers.get("content-type") || "application/octet-stream",
    });
  }
  return res.blob();
};

/**
 * Download media reliably.
 * Uses direct URL fetch + blob save.
 * Companion is intentionally not used for user-triggered gallery/dashboard downloads.
 */
export const downloadMediaFile = async ({ url, fileName }) => {
  if (!url) throw new Error("Missing media URL");

  const inferredName = (url.split("?")[0].split("/").pop() || "download.bin");
  const finalName = fileName || inferredName;

  const direct = await fetch(url);
  if (!direct.ok) {
    throw new Error(`Direct download failed: HTTP ${direct.status}`);
  }
  const blob = await readResponseAsBlob(direct);
  triggerBlobDownload(blob, finalName);
};

/**
 * Gallery/BulkUpload doesn't reject duplicates with an HTTP error — it uploads the rest
 * of the batch and marks skipped files IsArchived+RemovalReason="DUPLICATE_MEDIA" in the
 * response body, in the same order the files were sent (the backend never reorders the
 * list end-to-end). Match positionally to report which input file was which.
 *
 * @param {object} response   - axios response from a BulkUpload call
 * @param {string[]} fileNames - names of the files sent, in the order they were appended
 * @returns {{fileName: string, duplicate: boolean, existingMediaId: number|null, existingUrl: string|null}[]}
 */
export const parseBulkUploadDuplicates = (response, fileNames) => {
  const items = response?.data?.Result || response?.data?.result || [];
  return fileNames.map((fileName, idx) => {
    const item = items[idx];
    const isDuplicate = !!item && item.IsArchived === true && item.RemovalReason === "DUPLICATE_MEDIA";
    return {
      fileName,
      duplicate: isDuplicate,
      existingMediaId: isDuplicate ? (item.Id ?? null) : null,
      existingUrl: isDuplicate ? (item.VideoThumbnail || item.CompressedUrl || item.Url || null) : null,
    };
  });
};

/**
 * Bulk upload files to a specific album inside a gallery.
 *
 * @param {object} params
 * @param {FileList|File[]} params.files        - Files to upload
 * @param {string|number}   params.chatRoomId   - Chat room / event ID
 * @param {string|number}   params.eventGalleryId
 * @param {string|number}   params.galleryAlbumId
 * @param {string}          params.folderName
 * @param {string|number}   params.countryCode
 * @param {boolean}         [params.isCompressed=true]
 * @param {boolean}         [params.MaxCompressed=true]
 * @param {boolean}         [params.isWatermark=false]
 * @param {boolean}         [params.preventSelfie=false]
 * @param {boolean}         [params.forceCompress=false]
 * @param {function}        [onUploadProgress]  - Axios progress callback
 */
export const bulkUpload = async (
  {
    files,
    metaDateTimes: providedMetaDateTimes,
    chatRoomId,
    eventGalleryId,
    galleryAlbumId,
    folderName,
    countryCode,
    isCompressed = true,
    MaxCompressed = true,
    isWatermark = false,
    WMbyComanion = false,
    preventSelfie = false,
    forceCompress = false,
  },
  onUploadProgress
) => {
  const formData = new FormData();
  const fileArray = Array.from(files);
  const metaDateTimes = providedMetaDateTimes?.length
    ? providedMetaDateTimes
    : await resolveMetaDateTimes(fileArray, new Date());

  // Append each file (API field name: "files")
  fileArray.forEach((file) => formData.append("files", file));
  appendMetaDateTimes(formData, metaDateTimes);

  formData.append("chatRoomId", String(chatRoomId));
  if (eventGalleryId != null) formData.append("eventGalleryId", String(eventGalleryId));
  if (galleryAlbumId != null) formData.append("galleryAlbumId", String(galleryAlbumId));
  if (folderName != null) formData.append("folderName", normalizeUploadFolderName(folderName));
  if (countryCode != null) formData.append("countryCode", String(countryCode));
  formData.append("isCompressed", String(isCompressed));
  formData.append("MaxCompressed", String(MaxCompressed));
  formData.append("isUploadedUsingData", "false");

  // Only append optional fields when true (Postman doesn't send them when unchecked)
  if (isWatermark) formData.append("isWatermark", "true");
  if (WMbyComanion) formData.append("WMbyComanion", "true");
  if (preventSelfie) formData.append("preventSelfie", "true");
  if (forceCompress) formData.append("forceCompress", "true");

  return axiosClient.post("/Chat/Gallery/BulkUpload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
  });
};

/**
 * Wait for the browser to come back online.
 * Resolves immediately if already online.
 */
const waitForNetwork = () =>
  new Promise((resolve) => {
    if (navigator.onLine) return resolve();
    console.log("[Upload] Offline — waiting for network...");
    const handler = () => {
      window.removeEventListener("online", handler);
      console.log("[Upload] Network restored.");
      resolve();
    };
    window.addEventListener("online", handler);
  });

/**
 * Check if an error is a network / transient failure worth retrying.
 */
const isNetworkError = (err) => {
  if (!err) return false;
  // No response at all = network issue
  if (!err.response) return true;
  // Server errors (5xx) are transient
  if (err.response.status >= 500) return true;
  // 408 Request Timeout, 429 Too Many Requests
  if (err.response.status === 408 || err.response.status === 429) return true;
  return false;
};

/**
 * Upload a single file with network-aware retry.
 * - On network/transient errors: waits for network restoration, then retries (up to MAX_RETRIES).
 * - Uses exponential backoff between retries.
 * - Non-network errors (4xx) fail immediately.
 */
export const uploadSingleFile = async (
  {
    file,
    metaDateTime,
    chatRoomId,
    eventGalleryId,
    galleryAlbumId,
    folderName,
    countryCode,
    isCompressed = true,
    MaxCompressed = true,
    isWatermark = false,
    WMbyComanion = false,
    forceCompress = false,
  },
  onUploadProgress
) => {
  const metaDateTimes = metaDateTime
    ? [metaDateTime]
    : await resolveMetaDateTimes([file], new Date());

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("files", file);
    appendMetaDateTimes(fd, metaDateTimes);
    fd.append("chatRoomId", String(chatRoomId));
    if (eventGalleryId != null) fd.append("eventGalleryId", String(eventGalleryId));
    if (galleryAlbumId != null) fd.append("galleryAlbumId", String(galleryAlbumId));
    if (folderName != null) fd.append("folderName", normalizeUploadFolderName(folderName));
    if (countryCode != null) fd.append("countryCode", String(countryCode));
    fd.append("isCompressed", String(isCompressed));
    fd.append("MaxCompressed", String(MaxCompressed));
    fd.append("isUploadedUsingData", "false");
    if (isWatermark) fd.append("isWatermark", "true");
    if (WMbyComanion) fd.append("WMbyComanion", "true");
    if (forceCompress) fd.append("forceCompress", "true");
    return fd;
  };

  const MAX_RETRIES = 5;
  const BASE_DELAY_MS = 3000; // 3s, 6s, 12s, 24s, 48s
  const t0 = performance.now();

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await axiosClient.post("/Chat/Gallery/BulkUpload", buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      });
      const elapsed = ((performance.now() - t0) / 1000).toFixed(1);
      console.log(`[Upload] "${file.name}" uploaded in ${elapsed}s${attempt > 0 ? ` (attempt ${attempt + 1})` : ""} (${(file.size / 1e6).toFixed(2)} MB)`);
      return res;
    } catch (err) {
      const isLast = attempt === MAX_RETRIES;

      // Non-network errors (e.g. 400, 401, 403) — fail immediately
      if (!isNetworkError(err)) {
        console.error(`[Upload] "${file.name}" failed (non-retryable):`, err.response?.status, err.message);
        throw err;
      }

      if (isLast) {
        console.error(`[Upload] "${file.name}" failed after ${MAX_RETRIES + 1} attempts:`, err.message);
        throw err;
      }

      // Wait for network to come back (if offline)
      await waitForNetwork();

      // Exponential backoff
      const delay = BASE_DELAY_MS * Math.pow(2, attempt);
      console.warn(`[Upload] Retry ${attempt + 1}/${MAX_RETRIES} for "${file.name}" in ${delay / 1000}s...`);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
};

/**
 * Offload single-file upload to the local companion app.
 * Browser UI stays the same; companion performs final server transfer.
 */
export const uploadSingleFileViaCompanion = async (
  {
    file,
    metaDateTime,
    chatRoomId,
    eventGalleryId,
    galleryAlbumId,
    folderName,
    countryCode,
    isCompressed = true,
    MaxCompressed = true,
    isWatermark = false,
    WMbyComanion = false,
    forceCompress = false,
  },
  onUploadProgress
) => {
  const available = await checkCompanion();
  if (!available) {
    throw new Error("Companion not running");
  }

  const authToken = localStorage.getItem("token") || "";
  const apiBaseUrl = axiosClient.defaults.baseURL;
  if (!apiBaseUrl) {
    throw new Error("Missing API base URL");
  }

  const metaDateTimes = metaDateTime
    ? [metaDateTime]
    : await resolveMetaDateTimes([file], new Date());

  const formData = new FormData();
  formData.append("file", file);
  appendMetaDateTimes(formData, metaDateTimes);
  if (metaDateTimes.length > 0) {
    formData.append("metaDateTime", metaDateTimes[0]);
  }
  formData.append("chatRoomId", String(chatRoomId));
  if (eventGalleryId != null) formData.append("eventGalleryId", String(eventGalleryId));
  if (galleryAlbumId != null) formData.append("galleryAlbumId", String(galleryAlbumId));
  if (folderName != null) formData.append("folderName", normalizeUploadFolderName(folderName));
  if (countryCode != null) formData.append("countryCode", String(countryCode));
  formData.append("isCompressed", String(isCompressed));
  formData.append("MaxCompressed", String(MaxCompressed));
  formData.append("isWatermark", String(isWatermark));
  if (WMbyComanion) formData.append("WMbyComanion", "true");
  formData.append("forceCompress", String(forceCompress));
  formData.append("apiBaseUrl", String(apiBaseUrl));
  formData.append("authToken", authToken);

  console.log("[Upload] Companion single payload", {
    fileName: file.name,
    isWatermark: !!isWatermark,
    WMbyComanion: !!WMbyComanion,
    isCompressed: !!isCompressed,
    eventGalleryId,
    galleryAlbumId,
  });

  const jobId = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${COMPANION_URL}/upload`);
    let lastTs = performance.now();
    let lastLoaded = 0;
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const now = performance.now();
      const deltaSec = Math.max(0.001, (now - lastTs) / 1000);
      const deltaBytes = Math.max(0, e.loaded - lastLoaded);
      const speedMbps = (deltaBytes * 8) / (deltaSec * 1_000_000);
      const elapsedSec = Math.max(0.001, now / 1000);
      const avgMbps = (e.loaded * 8) / (elapsedSec * 1_000_000);

      lastTs = now;
      lastLoaded = e.loaded;

      onUploadProgress?.({
        phase: "handoff",
        loaded: e.loaded,
        total: e.total,
        speedMbps,
        avgMbps,
      });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          console.log(`[Upload] Companion job started for "${file.name}" (${parsed.jobId})`);
          resolve(parsed.jobId);
        } catch {
          reject(new Error("Companion returned invalid upload response"));
        }
      } else {
        reject(new Error(`Companion upload rejected: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Companion connection failed during upload handoff"));
    xhr.send(formData);
  });

  const doneInfo = await new Promise((resolve, reject) => {
    const es = new EventSource(`${COMPANION_URL}/upload-progress/${jobId}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.ratio !== undefined) {
          const ratio = Math.max(0, Math.min(1, data.ratio));
          onUploadProgress?.({
            phase: "companion",
            loaded: ratio * 1000,
            total: 1000,
            speedMbps: Number(data.speedMbps || 0),
            avgMbps: Number(data.avgMbps || 0),
          });
        }
        if (data.done) {
          es.close();
          resolve({
            duplicate: !!data.duplicate,
            existingMediaId: data.existingMediaId ?? null,
            existingUrl: data.existingUrl ?? null,
          });
        }
        if (data.error) {
          es.close();
          reject(new Error(data.error));
        }
      } catch {
        // ignore malformed events
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error("Companion upload progress stream lost"));
    };
  });

  return doneInfo; // { duplicate, existingMediaId, existingUrl }
};

/**
 * Offload a batch of image uploads to the local companion app.
 * Companion forwards the batch to backend Gallery/BulkUpload.
 */
export const uploadImageBatchViaCompanion = async (
  {
    files,
    metaDateTimes: providedMetaDateTimes,
    chatRoomId,
    eventGalleryId,
    galleryAlbumId,
    folderName,
    countryCode,
    isCompressed = true,
    MaxCompressed = true,
    isWatermark = false,
    WMbyComanion = false,
    forceCompress = false,
  },
  onUploadProgress
) => {
  const available = await checkCompanion();
  if (!available) {
    throw new Error("Companion not running");
  }

  const authToken = localStorage.getItem("token") || "";
  const apiBaseUrl = axiosClient.defaults.baseURL;
  if (!apiBaseUrl) {
    throw new Error("Missing API base URL");
  }

  const fileArray = Array.from(files || []);
  if (!fileArray.length) {
    throw new Error("No files provided for companion batch upload");
  }

  const metaDateTimes = providedMetaDateTimes?.length
    ? providedMetaDateTimes
    : await resolveMetaDateTimes(fileArray, new Date());

  const formData = new FormData();
  fileArray.forEach((file) => formData.append("files", file));
  appendMetaDateTimes(formData, metaDateTimes);
  if (metaDateTimes.length > 0) {
    formData.append("metaDateTime", metaDateTimes[0]);
  }
  formData.append("chatRoomId", String(chatRoomId));
  if (eventGalleryId != null) formData.append("eventGalleryId", String(eventGalleryId));
  if (galleryAlbumId != null) formData.append("galleryAlbumId", String(galleryAlbumId));
  if (folderName != null) formData.append("folderName", normalizeUploadFolderName(folderName));
  if (countryCode != null) formData.append("countryCode", String(countryCode));
  formData.append("isCompressed", String(isCompressed));
  formData.append("MaxCompressed", String(MaxCompressed));
  formData.append("isWatermark", String(isWatermark));
  if (WMbyComanion) formData.append("WMbyComanion", "true");
  formData.append("forceCompress", String(forceCompress));
  formData.append("apiBaseUrl", String(apiBaseUrl));
  formData.append("authToken", authToken);

  console.log("[Upload] Companion batch payload", {
    fileCount: fileArray.length,
    isWatermark: !!isWatermark,
    WMbyComanion: !!WMbyComanion,
    isCompressed: !!isCompressed,
    eventGalleryId,
    galleryAlbumId,
  });

  const jobId = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${COMPANION_URL}/upload-bulk`);
    let lastTs = performance.now();
    let lastLoaded = 0;
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const now = performance.now();
      const deltaSec = Math.max(0.001, (now - lastTs) / 1000);
      const deltaBytes = Math.max(0, e.loaded - lastLoaded);
      const speedMbps = (deltaBytes * 8) / (deltaSec * 1_000_000);
      const elapsedSec = Math.max(0.001, now / 1000);
      const avgMbps = (e.loaded * 8) / (elapsedSec * 1_000_000);

      lastTs = now;
      lastLoaded = e.loaded;

      onUploadProgress?.({
        phase: "handoff",
        loaded: e.loaded,
        total: e.total,
        speedMbps,
        avgMbps,
      });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const parsed = JSON.parse(xhr.responseText);
          resolve(parsed.jobId);
        } catch {
          reject(new Error("Companion returned invalid bulk upload response"));
        }
      } else {
        reject(new Error(`Companion bulk upload rejected: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Companion connection failed during bulk upload handoff"));
    xhr.send(formData);
  });

  const fileResults = await new Promise((resolve, reject) => {
    const es = new EventSource(`${COMPANION_URL}/upload-progress/${jobId}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.ratio !== undefined) {
          const ratio = Math.max(0, Math.min(1, data.ratio));
          onUploadProgress?.({
            phase: "companion",
            loaded: ratio * 1000,
            total: 1000,
            speedMbps: Number(data.speedMbps || 0),
            avgMbps: Number(data.avgMbps || 0),
          });
        }
        if (data.done) {
          es.close();
          resolve(data.fileResults || null);
        }
        if (data.error) {
          es.close();
          reject(new Error(data.error));
        }
      } catch {
        // ignore malformed events
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error("Companion bulk upload progress stream lost"));
    };
  });

  return { fileResults }; // fileResults: [{fileName, duplicate, existingMediaId, existingUrl}, ...] | null
};
